import { Button, IconButton, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import classNames from 'classnames';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback } from 'react';
import { NumericInput } from '../../Input/NumericInput';
import { RetirementOptions } from '../RetirementPage.hooks';
import { INITIAL_VALUES, schema } from './RetirementForm.hooks';
import styles from './RetirementForm.module.css';

export type RetirementFormProps = {
  initialValues?: Partial<RetirementOptions>;
  onSubmit: (options: RetirementOptions) => void;
  className?: string;
};

const HelpTooltip = () => (
  <Tooltip
    title={
      <div style={{ fontSize: '1.1em', lineHeight: 1.5 }}>
        <p>은퇴 후에는 보수적인 투자 전략을 권장합니다.</p>
        <ul style={{ paddingLeft: '20px' }}>
          <li>
            <strong>예금/적금:</strong> 2~3%
          </li>
          <li>
            <strong>안정적인 채권형:</strong> 3~4%
          </li>
          <li>
            <strong>배당주 중심:</strong> 4~5%
          </li>
        </ul>
        <p>은퇴 후에는 원금 보존이 중요하므로 보수적으로 3~4%를 권장합니다.</p>
      </div>
    }
    arrow
    placement="top"
  >
    <IconButton size="small">
      <InfoIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

export const RetirementForm = ({ initialValues, onSubmit, className }: RetirementFormProps) => {
  const handleSubmit = useCallback(
    (options: RetirementOptions) => {
      onSubmit(options);
    },
    [onSubmit]
  );

  const mergedInitialValues = { ...INITIAL_VALUES, ...initialValues };

  return (
    <Formik
      initialValues={mergedInitialValues}
      validationSchema={schema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ errors, setFieldValue }) => (
        <Form className={classNames(styles.root, className)}>
          <div className={styles.body}>
            <Field name="totalAsset">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="은퇴 시점 보유 자산 (원)"
                  placeholder="은퇴 시 모아둔 금액을 입력하세요"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => {
                    setFieldValue(field.name, values.floatValue);
                  }}
                />
              )}
            </Field>
            {errors.totalAsset && <div className={styles.error}>{errors.totalAsset}</div>}

            <Field name="monthlyExpense">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="월 생활비 (원)"
                  placeholder="은퇴 후 월 지출 예상액"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => {
                    setFieldValue(field.name, values.floatValue);
                  }}
                />
              )}
            </Field>
            {errors.monthlyExpense && <div className={styles.error}>{errors.monthlyExpense}</div>}

            <Field name="annualReturnRate">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      은퇴 후 예상 수익률 (%)
                      <HelpTooltip />
                    </div>
                  }
                  placeholder="보수적으로 3~4% 권장"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => {
                    setFieldValue(field.name, values.floatValue);
                  }}
                />
              )}
            </Field>
            {errors.annualReturnRate && (
              <div className={styles.error}>{errors.annualReturnRate}</div>
            )}

            <Field name="retirementAge">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="은퇴 나이 (세)"
                  placeholder="60"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => {
                    setFieldValue(field.name, values.floatValue);
                  }}
                />
              )}
            </Field>
            {errors.retirementAge && <div className={styles.error}>{errors.retirementAge}</div>}
          </div>
          <div className={styles.action}>
            <Button className={styles.button} variant="contained" type="submit">
              시뮬레이션 시작
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
